import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Shell } from "@/app/Shell";
import { RUNTIME_LABEL, exportUrl, importNotebook, nbKeys, type AssignmentView, type WorkspaceData } from "@/app/notebookQueries";
import { useMySubmissions } from "@/app/reviewQueries";
import { useSubmitFlow } from "@/routes/check/hooks";
import { STATUS_LABEL } from "@/routes/submission/model";
import { cellById, type Task } from "@/kernel/runner";
import { count } from "@/lib/plural";
import { Button } from "@/ui/Button";
import { Toast } from "@/ui/states";
import { TaskBlock } from "./readonly/TaskBlock";

const ddmm = (iso: string) => iso.slice(8, 10) + "." + iso.slice(5, 7);

/** S5 без раннера: среда задания (PyTorch и т.п.) в браузере не выполняется, а серверных
 *  раннеров ещё нет (SP6). Ноутбук виден целиком, скачивается, решается локально или в Colab,
 *  загружается обратно новой версией и отправляется ментору без автопроверки. */
export function OfflineWorkspace({ asg, ws, user }: { asg: AssignmentView; ws: WorkspaceData; user?: { name: string; role: string } }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<{ tone: "ok" | "err"; title: string; body: string } | null>(null);
  const tasks = asg.manifest.tasks as Task[];
  const runtime = RUNTIME_LABEL[asg.manifest.runtime] ?? asg.manifest.runtime;

  const onImport = async (file: File) => {
    try {
      const v = await importNotebook(asg.id, file);
      await qc.invalidateQueries({ queryKey: nbKeys.workspace(asg.id) });
      await qc.invalidateQueries({ queryKey: ["grading"] });
      setToast({ tone: "ok", title: `Версия ${v.n} загружена`, body: "Черновик заменён файлом. Теперь его можно отправить ментору." });
    } catch (e) {
      setToast({ tone: "err", title: "Файл не принят", body: e instanceof Error ? e.message : "Не удалось загрузить ноутбук." });
    }
  };

  return (
    <Shell wide user={user}>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-2.5">
        <span className="font-mono text-[11px] text-mut">
          домашки / {asg.track.toLowerCase()} · {asg.step_title.toLowerCase()}
        </span>
        <span className="font-mono text-[13px] font-semibold text-ink" data-testid="file-name">
          {asg.file_name}
        </span>
        <span className="rounded-sm border border-bd2 px-2 py-[2px] font-mono text-[11px] text-ink2">черновик</span>
        <span className="flex-1" />
        {asg.deadline && <span className="font-mono text-[11px] text-ink2">дедлайн {ddmm(asg.deadline)}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2.5 border-b border-bd bg-bg2 px-5 py-2.5">
        <span className="rounded-md border border-dashed border-warn px-2.5 py-1.5 font-mono text-[11px] text-warn" data-testid="env-offline">
          {runtime} · без раннера
        </span>
        <span className="flex-1" />
        <a href={exportUrl(asg.id)} download className="no-underline">
          <Button size="sm" variant="primary" data-testid="download">
            скачать .ipynb
          </Button>
        </a>
        <Button size="sm" onClick={() => fileInput.current?.click()} data-testid="upload">
          загрузить решение
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".ipynb,application/x-ipynb+json"
          className="hidden"
          data-testid="import-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onImport(f);
            e.target.value = "";
          }}
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-3 px-5 pb-10 pt-4">
          <div className="rounded-md border border-warn bg-warnsoft px-4 py-3 text-[13px] leading-[1.55] text-ink" role="status" data-testid="offline-banner">
            <strong>Автопроверки у этой домашки нет.</strong> Среда «{runtime}» в браузере не выполняется, а серверные раннеры ещё не подключены. Скачай ноутбук, реши его локально или в Colab, загрузи файл обратно и отправь ментору — он проверит код вручную.
          </div>
          <div className="font-mono text-[11.5px] text-mut">{count(tasks.length, "задача", "задачи", "задач")} · код и проверки из последней загруженной версии</div>
          {tasks.map((t) => (
            <TaskBlock key={t.n} task={t} code={cellById(ws.notebook, t.cell_id)} checks={cellById(ws.notebook, t.checks_cell_id)} />
          ))}
        </div>
        <aside className="border-t border-bd lg:border-l lg:border-t-0">
          <OfflineSubmit assignmentId={asg.id} />
        </aside>
      </div>
      {toast && (
        <div className="fixed bottom-4 right-4 z-30 w-[440px] max-w-[calc(100vw-32px)]">
          <Toast tone={toast.tone} title={toast.title} body={toast.body} action={{ label: "закрыть", onClick: () => setToast(null) }} />
        </div>
      )}
    </Shell>
  );
}

/** Отправка без прогона: можно ли и почему нет — только из eligibility сервера (правило 3). */
export function OfflineSubmit({ assignmentId }: { assignmentId: string }) {
  const submit = useSubmitFlow(assignmentId);
  const mine = useMySubmissions(assignmentId);
  const lastSub = mine.data?.[0];
  return (
    <div className="flex flex-col gap-2.5 px-4 py-4" data-testid="offline-submit">
      <div className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">отправка ментору</div>
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[12.5px] leading-[1.5] text-ink2">
        {(submit.elig?.conditions ?? []).map((c) => (
          <li key={c.key} className="flex gap-2">
            <span aria-hidden className={`font-mono ${c.met ? "text-ok" : "text-mut"}`}>
              {c.met ? "✓" : "○"}
            </span>
            <span>{c.text}</span>
          </li>
        ))}
      </ul>
      {(submit.submitted || lastSub) && (
        <Link to="/notebook/$assignmentId/review" params={{ assignmentId }} className="font-mono text-[11px] text-acc underline-offset-2 hover:underline" data-testid="to-review">
          {lastSub && !submit.submitted ? `сдача v${lastSub.iteration} · ${STATUS_LABEL[lastSub.status]} · ` : ""}к ревью →
        </Link>
      )}
      {submit.submitted ? (
        <p className="m-0 font-mono text-[11px] leading-[1.5] text-ok" data-testid="submitted">
          Отправлено ментору · на проверке
        </p>
      ) : (
        <>
          <Button variant={submit.eligible ? "primary" : undefined} className="w-full justify-center" data-testid="submit" disabled={!submit.eligible || submit.sending} onClick={() => void submit.send()}>
            отправить ментору без автопроверки
          </Button>
          {!submit.eligible && (
            <p className="m-0 font-mono text-[11px] leading-[1.5] text-ink2" data-testid="submit-reason">
              {submit.reason}
            </p>
          )}
          {submit.error && (
            <p className="m-0 font-mono text-[11px] leading-[1.5] text-err" role="alert">
              {submit.error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
